import { Icon } from "@iconify/react";
import { Delete } from "@mui/icons-material";
import { Box, IconButton, Pagination, TextField } from "@mui/material";
import axios from "axios";
import JoditEditor from "jodit-react";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import CustomLoader from "../components/CustomLoader";
import TransitionsModal from "../components/Modal";
import { deleteApi, getApi, postApi } from "../config/configAxios";
import { useAuthorize } from "../hooks/useAuthorize";
import AdminLayout from "../layouts/AdminLayout";
import store from "../redux/store";
import { config } from "../utils/JoditConfig";
import { shortenedText } from "../utils/textClamp";

const BitNews = () => {
  const hasPermission = useAuthorize();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    file: null,
    lat: "",
    long: "",
    content: "",
  });
  const [id, setId] = useState("");
  const [openStore, setOpenStore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      let url = `/bit-news?page=${page}&search=${search}`;
      const response = await getApi(url);
      setData(response.data);
      setTotalPages(response.data.last_page);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "There was an error fetching the data!"
      );
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const state = store.getState();
    const token = state.auth.token;
    if (token) {
      // axios.defaults.headers.Authorization = `Bearer ${token}`;
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    setLoading(true);
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, search]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setPage(value); // Set the new page
  };

  const handleDelete = async (id) => {
    try {
      await deleteApi(`/bit-news/${id}`);
      setData(data.filter((officer) => officer.id !== id));
      toast.success("Deleted successfully");
    } catch (error) {
      toast.error("There was an error deleting the officer!");
    }
  };

  useEffect(() => {
    if (id) {
      (async () => {
        try {
          const response = await getApi(`/bit-news/${id}`);
          setFormData({
            title: response.data.title,
            content: response.data.content,
            id: response.data.id,
          });
        } catch (error) {
          toast.error("There was an error fetching the officer!");
          console.error("There was an error fetching the officer!", error);
        }
      })();
    }
  }, [id]);
  const onClose = () => {
    setOpenStore(false);
    setId("");
    setFormData({
      title: "",
      file: null,
      lat: "",
      long: "",
      content: "",
    });
    setIsSubmitting(false);
  };

  const onOpen = () => {
    setOpenStore(true);
    setId("");
    setFormData({
      title: "",
      file: null,
      lat: "",
      long: "",
      content: "",
    });
  };

  const handleChange = (event) => {
    const { name, value, files } = event.target;
    setFormData({
      ...formData,
      [name]: files ? files[0] : value,
    });
  };

  const getLocation = async () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        toast.error("Geolocation is not supported by your browser.");
        reject(new Error("Geolocation is not supported by your browser."));
      } else {
        const options = {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        };
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            resolve({ latitude, longitude });
            navigator.geolocation.clearWatch(watchId);
          },
          (error) => {
            toast.error("Unable to retrieve your location.");
            reject(error);
          },
          options
        );
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setErrors({});
    try {
      const location = await getLocation();

      if (id) {
        const formDataValue = new FormData();
        Object.keys(formData).forEach((key) => {
          formDataValue.append(key, formData[key]);
        });
        formDataValue.append("_method", "PUT");
        await postApi(`/bit-news/${id}`, formDataValue);
        toast.success("Updated successfully");
      } else {
        const formDataValue = new FormData();
        const updatedFormData = {
          ...formData,
          lat: location.latitude,
          long: location.longitude,
        };
        Object.keys(updatedFormData).forEach((key) => {
          formDataValue.append(key, updatedFormData[key]);
        });
        formDataValue.append("user_id", user?.id);

        await postApi("/bit-news", formDataValue);
        toast.success("Added successfully");
      }
      onClose();
      fetchData();
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(
          `There was an error ${id ? "updating" : "creating"} the officer!`
        );
        console.error(
          `There was an error ${id ? "updating" : "creating"} the officer!`,
          error
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const canEditOrDelete = (createdAt) => {
    const createdDate = new Date(createdAt);
    const currentDate = new Date();
    const diffMinutes = (currentDate - createdDate) / (1000 * 60);
    return diffMinutes <= 30;
  };

  return (
    <AdminLayout title={"Bit News"}>
      <div className="flex justify-between items-center p-3">
        <div className="flex justify-between items-center gap-3">
          <h1 className="text-gray-700 font-medium">Bit News</h1>
          {hasPermission("Bit Officer") && (
            <button className="btn-indigo" onClick={onOpen}>
              Add new
            </button>
          )}
        </div>
        <TextField
          label="Search by Title"
          variant="outlined"
          size="small"
          value={search}
          onChange={handleSearchChange}
        />
      </div>
      {loading ? (
        <CustomLoader />
      ) : (
        <div>
          <div className="table-wrap">
            <table className="table-main shadow-md">
              <thead className="table-h">
                <tr>
                  <th className="table-h">SN</th>
                  <th className="table-h">Image</th>
                  <th className="table-h">Title</th>
                  <th className="table-h">Action</th>
                </tr>
              </thead>
              <tbody className="table-b">
                {data?.data?.map((item, index) => (
                  <tr key={item.id}>
                    <td className="table-b">{index + 1}</td>
                    <td className="table-b">
                      {item.file && (
                        <img
                          className="w-12 h-12 rounded object-cover"
                          src={`${process.env.REACT_APP_API_FILE_URL}${item.file}`}
                          alt=""
                        />
                      )}
                    </td>
                    <td className="table-b">{shortenedText(item.title, 30)}</td>
                    <td className="table-b">
                      <Box display="flex">
                        {hasPermission("Admin") ||
                        canEditOrDelete(item.created_at) ? (
                          <>
                            <IconButton
                              className="mr-3"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Delete className="text-red-500" />
                            </IconButton>
                            <IconButton
                              className="mr-3"
                              onClick={() => {
                                setId(item.id);
                                setFormData(item);
                                setOpenStore(true);
                              }}
                            >
                              <Icon
                                icon={"bxs:edit"}
                                className="text-blue-500"
                              />
                            </IconButton>
                          </>
                        ) : null}
                        {hasPermission("Admin") && (
                          <Link
                            to={`https://maps.google.com?q=${item.lat},${item.long}`}
                            target="_blank"
                          >
                            <IconButton className="mr-3">
                              <Icon icon={"bxs:map"} className="text-red-500" />
                            </IconButton>
                          </Link>
                        )}
                      </Box>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-10 text-center mx-auto">
        <Pagination
          count={totalPages} // Total number of pages
          page={page} // Current page
          onChange={handlePageChange} // Handler for page change
          color="primary"
          className="mt-4  mx-auto"
        />
      </div>

      {/* Modal */}
      <TransitionsModal
        title={id ? "Edit News" : "New News"}
        open={openStore}
        setOpen={setOpenStore}
      >
        <form onSubmit={handleSubmit}>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-6">
            {[
              { label: "Title", name: "title", type: "text" },
              { label: "File", name: "file", type: "file" },
              { label: "Content", name: "content", type: "text" },
            ].map(({ label, name, type, optional }) =>
              name === "content" ? (
                <div className="md:col-span-2" key={name}>
                  <label htmlFor={name} className="font-semibold text-sm">
                    {label} {optional ? "(optional)" : ""}
                  </label>
                  <JoditEditor
                    config={config}
                    tabIndex={1}
                    value={formData[name]}
                    onBlur={(newContent) =>
                      setFormData((prevData) => ({
                        ...prevData,
                        content: newContent,
                      }))
                    }
                  />
                  {errors[name] && (
                    <span className="text-red-500 text-sm">
                      {errors[name][0]}
                    </span>
                  )}
                </div>
              ) : (
                <div className="md:col-span-1" key={name}>
                  <label htmlFor={name} className="font-semibold text-sm">
                    {label} {optional ? "(optional)" : ""}
                  </label>
                  <input
                    type={type}
                    id={name}
                    name={name}
                    value={name !== "file" ? formData[name] : undefined}
                    onChange={handleChange}
                    className="form-control"
                    required={name === "file" ? !id && !optional : true}
                  />
                  {errors[name] && (
                    <span className="text-red-500 text-sm">
                      {errors[name][0]}
                    </span>
                  )}
                </div>
              )
            )}
            <div className="md:col-span-2">
              <button
                disabled={isSubmitting}
                type="submit"
                className="btn-green w-full"
                data-ripple-light="true"
              >
                {isSubmitting ? (
                  <BeatLoader color="#fff" />
                ) : id ? (
                  "Update"
                ) : (
                  "Create"
                )}
              </button>
            </div>
          </div>
        </form>
      </TransitionsModal>
    </AdminLayout>
  );
};

export default BitNews;
